/**
 * ╔══════════════════════════════════════════════════════════════════╗
 * ║  ABILITIES REGISTRY — CASL-compatible RBAC                       ║
 * ║                                                                  ║
 * ║  Subjects are HIERARCHICAL dotted paths derived from the         ║
 * ║  NAV_REGISTRY (e.g. "ops.crm.customers"). Granting an ability    ║
 * ║  on an ancestor cascades to all descendants — analogous to how   ║
 * ║  the CASL "manage" action cascades over the four CRUD verbs.     ║
 * ║                                                                  ║
 * ║  Two cascades, one model:                                        ║
 * ║    • SUBJECT cascade: "ops" implies "ops.crm.customers", etc.    ║
 * ║    • ACTION cascade : "manage" implies view/create/update/delete ║
 * ║                                                                  ║
 * ║  Plus per-node EXTRA actions ("approve", "export", "impersonate" ║
 * ║  …) declared on the registry node — only rendered in the matrix  ║
 * ║  for nodes that opt-in, so the grid stays sane.                  ║
 * ╚══════════════════════════════════════════════════════════════════╝
 */
import { z } from "zod";
import { subjectAncestors as _subjectAncestors } from "@/config/navRegistry.helpers";

// ─────────────────────────────────────────────────────────────────────
// SUBJECTS — open string set (validated at runtime against the
// registry by the matrix UI). The wildcard "all" is always valid.
// ─────────────────────────────────────────────────────────────────────

export const SubjectSchema = z
  .string()
  .min(1)
  .regex(/^([a-z][a-z0-9-]*)(\.[a-z][a-z0-9-]*)*$|^all$/, {
    message: "Subjects must be dotted lowercase ids (or 'all').",
  });
export type Subject = string;

/** Re-export the ancestor walker so consumers don't need a second import. */
export const subjectAncestors = _subjectAncestors;

// ─────────────────────────────────────────────────────────────────────
// ACTIONS
// ─────────────────────────────────────────────────────────────────────

export const CORE_ACTIONS = ["view", "create", "update", "delete", "manage"] as const;
export const EXTRA_ACTIONS = [
  "export",
  "approve",
  "assign",
  "impersonate",
  "bulk-edit",
] as const;
export const ACTIONS = [...CORE_ACTIONS, ...EXTRA_ACTIONS] as const;

export const ActionSchema = z.enum(ACTIONS);
export type Action = z.infer<typeof ActionSchema>;
/** Alias for downstream type ergonomics. */
export type ActionId = Action;

export const ACTION_LABELS: Record<Action, string> = {
  view: "View",
  create: "Create",
  update: "Edit",
  delete: "Delete",
  manage: "Manage",
  export: "Export",
  approve: "Approve",
  assign: "Assign",
  impersonate: "Impersonate",
  "bulk-edit": "Bulk Edit",
};

export const ACTION_SHORT: Record<Action, string> = {
  view: "V",
  create: "C",
  update: "E",
  delete: "D",
  manage: "★",
  export: "↧",
  approve: "✓",
  assign: "→",
  impersonate: "🕶",
  "bulk-edit": "≡",
};

/** CRUD-only actions rendered as default columns under each row. */
export const CRUD_ACTIONS: readonly Exclude<Action, "manage" | typeof EXTRA_ACTIONS[number]>[] = [
  "view",
  "create",
  "update",
  "delete",
];

// ─────────────────────────────────────────────────────────────────────
// Ability rule shape
// ─────────────────────────────────────────────────────────────────────

export const AbilityRuleSchema = z.object({
  subject: SubjectSchema,
  action: ActionSchema,
  conditions: z.record(z.unknown()).nullable().optional(),
  inverted: z.boolean().optional(),
});
export type AbilityRule = z.infer<typeof AbilityRuleSchema>;

/** Stable key for a (subject, action) pair. */
export const abilityKey = (subject: Subject, action: Action) => `${subject}:${action}`;

/**
 * Does the rule set grant (subject, action)?
 *
 * Honors BOTH cascades:
 *   • action  : "manage" implies view/create/update/delete
 *   • subject : a grant on any ancestor (or "all") implies the descendant
 */
export const hasAbility = (
  rules: AbilityRule[] | null | undefined,
  subject: Subject,
  action: Action,
): boolean => {
  if (!rules?.length) return false;
  const ancestors = new Set(_subjectAncestors(subject));
  for (const r of rules) {
    if (r.inverted) continue;
    const subjMatch = ancestors.has(r.subject);
    const actMatch = r.action === action || r.action === "manage";
    if (subjMatch && actMatch) return true;
  }
  return false;
};
